import type { ApiErrorBody } from './types';

/**
 * Normalized transport error. Both the real and mock clients throw this so the
 * UI handles failures uniformly (toasts, retries, field-level errors).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.detail = body.detail;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isServer(): boolean {
    return this.status >= 500;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * O 409 de uma conversa do WhatsApp que o console tentou renomear ou excluir: o
 * histórico do bot pertence ao WhatsApp, e aqui só se lê e se fixa.
 */
export const WHATSAPP_READONLY = 'whatsapp_readonly';

export const WHATSAPP_READONLY_MESSAGE =
  'Conversas do WhatsApp não podem ser renomeadas nem excluídas.';

/**
 * As frases pt-BR que o console impõe por código do envelope, no lugar do texto
 * que o servidor mandar: o código é o contrato estável, a redação é nossa.
 */
const MESSAGE_BY_CODE: ReadonlyMap<string, string> = new Map([
  [WHATSAPP_READONLY, WHATSAPP_READONLY_MESSAGE],
]);

/** A frase do console para `code`, ou `undefined` quando vale a do servidor. */
export function messageForCode(code: string): string | undefined {
  return MESSAGE_BY_CODE.get(code);
}

/**
 * Os códigos do envelope que o PLANO produz na borda (services/errors.py):
 * `plan_capability_required` (403 — a superfície é paga) e
 * `plan_quota_exceeded` (429 — a cota mensal do gratuito acabou).
 */
export const PLAN_CAPABILITY_REQUIRED = 'plan_capability_required';
export const PLAN_QUOTA_EXCEEDED = 'plan_quota_exceeded';

export type PlanRefusal = 'capability' | 'quota';

/**
 * A recusa é de plano? O console ancora o CTA de upgrade no CÓDIGO, nunca no
 * status solto nem na mensagem: 403 e 429 genéricos não vendem nada.
 */
export function planRefusalOf(error: unknown): PlanRefusal | null {
  if (!isApiError(error)) return null;
  if (error.code === PLAN_CAPABILITY_REQUIRED) return 'capability';
  if (error.code === PLAN_QUOTA_EXCEEDED) return 'quota';
  return null;
}
